import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/firebaseconfig';
import { FaMapMarkerAlt, FaPhone, FaTools } from 'react-icons/fa';

const RepairCentersList = () => {
  const [centers, setCenters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchCenters = async () => {
      try {
        // Query for users with role 'serviceOwner'
        const q = query(collection(db, 'users'), where('role', '==', 'serviceOwner'));
        const querySnapshot = await getDocs(q);
        
        const centersList = [];
        querySnapshot.forEach((doc) => {
          centersList.push({
            id: doc.id,
            displayName: doc.data().displayName || 'Service Provider',
            businessName: doc.data().businessName || 'Unnamed Business',
            businessAddress: doc.data().businessAddress || 'Address not provided',
            phone: doc.data().phone || 'Phone not provided',
            rating: doc.data().rating || 0,
            reviewCount: doc.data().reviewCount || 0,
            services: doc.data().services || []
          });
        });
        
        setCenters(centersList);
      } catch (error) {
        console.error("Error fetching centers:", error);
        setError("Failed to load repair centers. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchCenters();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent align-[-0.125em]"></div>
          <p className="mt-2 text-gray-700">Loading repair centers...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-6 rounded-lg shadow-md max-w-md">
          <div className="text-red-600 text-center mb-4">
            <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h2 className="text-xl font-semibold mt-2">Error</h2>
          </div>
          <p className="text-center">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Repair Centers</h1>
          <p className="mt-2 text-lg text-gray-600">Find trusted repair centers for your devices</p>
        </div>
        
        {centers.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <FaTools className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-lg font-medium text-gray-900">No repair centers found</h3>
            <p className="mt-1 text-gray-500">Check back later for available service centers.</p>
          </div>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {centers.map(center => (
              <Link 
                key={center.id}
                to={`/repair-center/${center.id}`}
                className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow duration-300"
              >
                <div className="p-6">
                  <h2 className="text-xl font-bold text-gray-900">{center.businessName}</h2>
                  <p className="text-sm text-gray-500">{center.displayName}</p>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-start">
                      <FaMapMarkerAlt className="flex-shrink-0 h-5 w-5 text-blue-500 mt-0.5" />
                      <p className="ml-2 text-sm text-gray-600">{center.businessAddress}</p>
                    </div>
                    
                    <div className="flex items-center">
                      <FaPhone className="flex-shrink-0 h-5 w-5 text-blue-500" />
                      <p className="ml-2 text-sm text-gray-600">{center.phone}</p>
                    </div>
                  </div>
                  
                  {center.services && center.services.length > 0 && (
                    <div className="mt-4">
                      <h3 className="text-sm font-medium text-gray-900">Services:</h3>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {center.services.slice(0, 3).map((service, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {service}
                          </span>
                        ))}
                        {center.services.length > 3 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            +{center.services.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <div className="mt-4 flex items-center">
                    <div className="flex items-center">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <svg
                          key={star}
                          className={`h-5 w-5 ${
                            star <= Math.round(center.rating) ? 'text-yellow-400' : 'text-gray-300'
                          }`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                        </svg>
                      ))}
                    </div>
                    <p className="ml-2 text-sm text-gray-600">
                      {center.rating.toFixed(1)} ({center.reviewCount} reviews)
                    </p>
                  </div>
                  
                  <div className="mt-6">
                    <span className="inline-flex w-full justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700">
                      View Details
                    </span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RepairCentersList;